import { Builder } from '@builder.io/react';
import cn from 'classnames';
import { FC } from 'react';
import { match } from 'ts-pattern';

import { COLOR_NAME_WHITE } from '@/shared/constants/colors';
import {
  actionButtonInput,
  backgroundColorInput,
  componentLinkIdInput,
} from '@/shared/inputs';
import { getBuilderSpacingClass } from '@/shared/spacing';
import { isBuilderStringPresent } from '@/shared/text';
import { SharedBuilderComponent } from '@/shared/types';
import { getBuilderIOClassName } from '@/shared/utils/getBuilderIOClassName';

import { Action, ActionVariant, BaseAction } from '../Action';
import sharedStyle from '../shared.module.scss';
import { Header } from './Header';
import { List, ListItem } from './List';
import { SingleContentType } from './type';

interface BuilderInput extends SharedBuilderComponent {
  componentLinkId?: string;
  type: SingleContentType;
  headline?: string;
  description?: string;
  descriptionTextSize?: string;
  // TODO: Deprecated expandText
  expandText?: boolean;
  fullWidth?: boolean;
  action: BaseAction;
  actionVariant?: ActionVariant;
  listItems?: ListItem[];
}

type Props = BuilderInput & {
  locale: string;
};

export const SingleContent: FC<Props> = ({
  componentLinkId,
  type = 'standard',
  headline,
  description,
  descriptionTextSize,
  // TODO: Deprecated expandText
  expandText,
  fullWidth,
  action,
  actionVariant = ActionVariant.NONE,
  listItems,
  attributes,
  backgroundColorClassName,
  builderBlock,
  builderState,
  locale,
}) => {
  const builderStyles = getBuilderIOClassName(attributes);
  const spacingStyles = getBuilderSpacingClass(builderBlock, builderState);

  // When open page in editor or LivePreview mode, Builder doesn't run dataFetcher
  // causing locale value undefined. So, we use builderState locale value when
  // using component in editor and leave the locale when we generate static content
  const blockLocale = locale || builderState?.context.locale;
  const actionTopSpacing = match(type)
    .with('number', () => {
      if (isBuilderStringPresent(headline, locale)) return 'u-m-t-xxl-6';
      if (isBuilderStringPresent(description, locale)) return 'u-m-t-xxl-6';
      if (listItems && listItems.length > 0) return 'u-m-t-xxl-6';
      return '';
    })
    .otherwise(() => {
      if (isBuilderStringPresent(headline, locale)) return 'u-m-t-xl';
      if (isBuilderStringPresent(description, locale)) return 'u-m-t-xl';
      return '';
    });

  const listTopSpacing = match({
    headlinePresent: isBuilderStringPresent(headline, locale),
    descriptionPresent: isBuilderStringPresent(description, locale),
  })
    .with({ headlinePresent: false, descriptionPresent: false }, () => '')
    .otherwise(() => 'u-m-t-xxl-6');

  return (
    <section
      data-testid="singleContent"
      {...attributes}
      id={componentLinkId}
      className={cn(
        sharedStyle.section,
        builderStyles,
        spacingStyles,
        backgroundColorClassName
      )}
    >
      <div className={cn('u-wrapper')}>
        <Header
          type={type}
          headline={headline}
          description={description}
          descriptionTextSizeOverride={`u-typography-${descriptionTextSize}`}
          // TODO: Deprecated expandText when no pages using it
          fullWidth={expandText !== undefined ? expandText : fullWidth}
          locale={blockLocale}
        />
        <List
          type={type}
          items={listItems}
          locale={blockLocale}
          spacingClassName={listTopSpacing}
        />
        <Action
          {...action}
          variant={actionVariant}
          className={actionTopSpacing}
          locale={blockLocale}
        />
      </div>
    </section>
  );
};

export function dataFetcher(locale: string) {
  return async () => ({
    locale,
  });
}

export const NAME = 'SingleContent';

export function componentRegister() {
  Builder.registerComponent(SingleContent, {
    name: NAME,
    inputs: [
      componentLinkIdInput(),
      {
        name: 'type',
        friendlyName: 'Block Type',
        type: 'string',
        defaultValue: 'standard',
        enum: ['standard', 'number'],
      },
      {
        name: 'headline',
        friendlyName: 'HeadLine',
        type: 'CatawikiEditor',
        defaultValue:
          'Sample Single <span class="u-typography-h1-serif u-color-brand">Content</span>',
      },
      {
        name: 'description',
        friendlyName: 'Description',
        type: 'CatawikiEditor',
        defaultValue:
          'Long description telling visitor what do we want to highlight in this month',
      },
      {
        name: 'descriptionTextSize',
        friendlyName: 'Description Text Size',
        type: 'string',
        showIf: `options.get('type') === 'standard'`,
        defaultValue: 'h3',
        enum: ['h3', 'h4'],
      },
      {
        name: 'fullWidth',
        friendlyName: 'Show content in full width',
        helperText: 'Show content in full width or limit to 800px',
        type: 'boolean',
        showIf: `options.get('type') === 'standard'`,
        defaultValue: true,
      },
      backgroundColorInput(COLOR_NAME_WHITE),
      ...actionButtonInput({}),
      {
        name: 'listItems',
        friendlyName: 'List Items',
        type: 'list',
        subFields: [
          {
            name: 'number',
            type: 'CatawikiEditor',
          },
          {
            name: 'content',
            type: 'CatawikiEditor',
            required: true,
            defaultValue: 'Content showing step to visitor',
          },
        ],
      },
    ],
    noWrap: true,
  });
  Builder.registerComponent(SingleContent, {
    name: `v2:${NAME}`,
    inputs: [
      componentLinkIdInput(),
      {
        name: 'type',
        friendlyName: 'Block Type',
        type: 'string',
        defaultValue: 'standard',
        enum: ['standard', 'number'],
      },
      {
        name: 'headline',
        friendlyName: 'HeadLine',
        type: 'html',
        defaultValue:
          'Sample Single <span class="u-typography-h1-serif u-color-brand">Content</span>',
      },
      {
        name: 'description',
        friendlyName: 'Description',
        type: 'html',
        defaultValue:
          'Long description telling visitor what do we want to highlight in this month',
      },
      {
        name: 'descriptionTextSize',
        friendlyName: 'Description Text Size',
        type: 'string',
        showIf: `options.get('type') === 'standard'`,
        defaultValue: 'h3',
        enum: ['h3', 'h4'],
      },
      {
        name: 'fullWidth',
        friendlyName: 'Show content in full width',
        helperText: 'Show content in full width or limit to 800px',
        type: 'boolean',
        showIf: `options.get('type') === 'standard'`,
        defaultValue: true,
      },
      backgroundColorInput(COLOR_NAME_WHITE),
      ...actionButtonInput({ useCatawikiPlainEditor: false }),
      {
        name: 'listItems',
        friendlyName: 'List Items',
        type: 'list',
        subFields: [
          {
            name: 'number',
            type: 'html',
          },
          {
            name: 'content',
            type: 'html',
            required: true,
            defaultValue: 'Content showing step to visitor',
          },
        ],
      },
    ],
    noWrap: true,
  });
  return NAME;
}

export const componentDataFetchers = (locale: string) => ({
  [NAME]: dataFetcher(locale),
  [`v2:${NAME}`]: dataFetcher(locale),
});
